import { useState } from "react";
import { v4 } from "uuid";
import t from "@/lang/lang";
import {
  Address,
  newAddress,
  oneLine,
} from "@/services/backend/addresses/types/address";
import { BuildingWithoutAreas } from "@/services/backend/buildings/types/building";
import {
  GfsAreaSnapshot,
  GfsAreaAssessment,
  GfsAssessment,
  GfsBuildingSnapshot,
  GfsLevelSnapshot,
} from "@/services/backend/gfs/assessment/types";
import { useGfsAssessmentImportBuildingMutation } from "@/services/backend/gfs/assessment/api";
import { Button } from "@/shared/components/ui/button";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
} from "@/shared/components/ui/combobox";
import { Label } from "@/shared/components/ui/label";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";

export const GFS_AREA_TYPE_INSIDE = "INSIDE";
export const GFS_AREA_TYPE_OUTSIDE = "OUTSIDE";

export type GfsAreaType =
  typeof GFS_AREA_TYPE_INSIDE | typeof GFS_AREA_TYPE_OUTSIDE;

type GfsBuildingSelectorProps = {
  buildings?: GfsBuildingSnapshot[];
  selectedBuilding: GfsBuildingSnapshot | undefined;
  handleBuildingSelect: (value: string) => void;
  onBuildingCreateCallback?: (
    value: GfsBuildingSnapshot,
  ) => boolean | void | Promise<boolean | void>;
  monitorCountByBuildingId?: Record<string, number>;
  assessmentId?: string;
  customerBuildings?: BuildingWithoutAreas[];
  disabled?: boolean;
  selectedBuildingValue?: string;
  newBuildingAddress?: Address;
  onCustomerBuildingSelect?: (sourceBuildingId: string) => void | Promise<void>;
  onCustomerBuildingImported?: (
    assessment: GfsAssessment,
    importedBuildingId: string,
  ) => void;
};

type GfsBuildingSelectorOption = GfsBuildingSnapshot & {
  sourceBuildingId?: string;
};

export function GfsBuildingSelector({
  buildings = [],
  selectedBuilding,
  handleBuildingSelect,
  onBuildingCreateCallback,
  monitorCountByBuildingId = {},
  assessmentId,
  customerBuildings = [],
  disabled = false,
  selectedBuildingValue,
  newBuildingAddress,
  onCustomerBuildingSelect,
  onCustomerBuildingImported,
}: GfsBuildingSelectorProps) {
  const [newBuildingName, setNewBuildingName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importBuilding, importState] =
    useGfsAssessmentImportBuildingMutation();

  const handleSubmit = async () => {
    const name = newBuildingName.trim();
    if (!name || isCreating) return;

    const building: GfsBuildingSnapshot = {
      id: v4(),
      name,
      address: newBuildingAddress ? { ...newBuildingAddress } : newAddress(),
      levels: [],
      areas: [],
      takenAt: new Date().toISOString(),
    };

    setIsCreating(true);
    try {
      const succeeded = await onBuildingCreateCallback?.(building);
      if (succeeded === false) return;
      setNewBuildingName("");
    } finally {
      setIsCreating(false);
    }
  };

  const handleSelect = (buildingId: string) => {
    handleBuildingSelect(buildingId);
  };

  const handleCustomerSelect = async (sourceBuildingId: string) => {
    if (onCustomerBuildingSelect) {
      await onCustomerBuildingSelect(sourceBuildingId);
      return;
    }
    if (!assessmentId || isImporting) return;
    setIsImporting(true);
    try {
      const nextAssessment = await importBuilding({
        assessmentId,
        sourceBuildingId,
      }).unwrap();
      const imported = nextAssessment.buildings.find(
        (building) => building.sourceId === sourceBuildingId,
      );
      if (imported) {
        if (onCustomerBuildingImported) {
          onCustomerBuildingImported(nextAssessment, imported.id);
        } else {
          handleSelect(imported.id);
        }
      }
    } catch {
      return;
    } finally {
      setIsImporting(false);
    }
  };

  const toBuildingLabel = (building: GfsBuildingSnapshot): string => {
    const monitorCount = monitorCountByBuildingId[building.id] ?? 0;
    const address = oneLine(building.address);
    return [building.name, address, `(${monitorCount})`]
      .filter(Boolean)
      .join(" – ");
  };

  const importedSourceIds = new Set(
    buildings.flatMap((building) =>
      building.sourceId ? [building.sourceId] : [],
    ),
  );
  const availableCustomerBuildings = customerBuildings.filter(
    (building) => !importedSourceIds.has(building.id),
  );
  const customerOptions: GfsBuildingSelectorOption[] =
    availableCustomerBuildings.map((building) => ({
      id: `customer:${building.id}`,
      sourceBuildingId: building.id,
      name: building.name,
      address: { ...building.address },
      levels: [],
      areas: [],
      takenAt: new Date().toISOString(),
    }));
  const existingOptions: GfsBuildingSelectorOption[] = buildings;
  const allOptions = [...existingOptions, ...customerOptions];
  const selectedOption =
    allOptions.find(
      (building) =>
        building.id === (selectedBuildingValue ?? selectedBuilding?.id),
    ) ?? null;

  return (
    <div className="grid items-start gap-1.5">
      <Label htmlFor="gfs-building-selector">{t("Gebäude")}</Label>
      <Combobox<GfsBuildingSelectorOption>
        value={selectedOption}
        disabled={disabled || isImporting}
        onValueChange={(building) => {
          setNewBuildingName("");
          if (!building) return;
          if (building.sourceBuildingId) {
            void handleCustomerSelect(building.sourceBuildingId);
            return;
          }
          handleSelect(building.id);
        }}
        onInputValueChange={setNewBuildingName}
        itemToStringLabel={toBuildingLabel}
        itemToStringValue={toBuildingLabel}
      >
        <ComboboxInput
          id="gfs-building-selector"
          aria-label={t("Gebäude")}
          className="h-10 w-full max-w-[320px]"
          placeholder={
            selectedOption
              ? toBuildingLabel(selectedOption)
              : t("Gebäude auswählen...")
          }
          showClear
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              handleSubmit();
            }
          }}
        />
        <ComboboxContent className="w-[320px]">
          <ComboboxEmpty>
            <div className="flex flex-col gap-3 px-6 text-sm">
              <span>{t("Gebäude nicht vorhanden.")}</span>
              <Button
                onClick={handleSubmit}
                variant="default"
                disabled={
                  disabled ||
                  !newBuildingName.trim() ||
                  isCreating ||
                  isImporting
                }
              >
                {t("Hinzufügen")}
              </Button>
            </div>
          </ComboboxEmpty>
          <ComboboxList>
            {existingOptions.length > 0 && (
              <ComboboxGroup>
                <ComboboxLabel>{t("Existierende Gebäude")}</ComboboxLabel>
                {existingOptions.map((building) => (
                  <ComboboxItem value={building} key={building.id}>
                    {toBuildingLabel(building)}
                  </ComboboxItem>
                ))}
              </ComboboxGroup>
            )}
            {customerOptions.length > 0 && (
              <ComboboxGroup>
                <ComboboxLabel>{t("Kundengebäude")}</ComboboxLabel>
                {customerOptions.map((building) => (
                  <ComboboxItem value={building} key={building.id}>
                    {building.name} –{" "}
                    {oneLine(building.address) || t("Ohne Adresse")}
                  </ComboboxItem>
                ))}
              </ComboboxGroup>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
      {isImporting && (
        <span className="text-muted-foreground text-sm">
          {t("Kundengebäude wird geladen...")}
        </span>
      )}
      {importState.error && <RTKQueryErrorAlert error={importState.error} />}
    </div>
  );
}

type GfsLevelSelectorProps = {
  levels?: GfsLevelSnapshot[];
  selectedLevel: GfsLevelSnapshot | undefined;
  handleLevelSelect: (value: string) => void;
  buildingId?: string;
  createLevelType: GfsAreaType;
  onLevelCreatedCallback?: (
    level: GfsLevelSnapshot,
  ) => boolean | void | Promise<boolean | void>;
  monitorCountByLevelId?: Record<string, number>;
  noLevelMonitorCount?: number;
  isNoLevelSelected?: boolean;
  disabled?: boolean;
};

export function GfsLevelSelector({
  levels = [],
  selectedLevel,
  handleLevelSelect,
  buildingId = "",
  createLevelType,
  onLevelCreatedCallback,
  monitorCountByLevelId = {},
  noLevelMonitorCount = 0,
  isNoLevelSelected = false,
  disabled = false,
}: GfsLevelSelectorProps) {
  const [newLevelName, setNewLevelName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const handleSubmit = async () => {
    const name = newLevelName.trim();
    if (!name || !buildingId || isCreating) return;

    const level: GfsLevelSnapshot = {
      id: v4(),
      name,
      type: createLevelType,
    };

    setIsCreating(true);
    try {
      const succeeded = await onLevelCreatedCallback?.(level);
      if (succeeded === false) return;
      setNewLevelName("");
    } finally {
      setIsCreating(false);
    }
  };

  const toLevelLabel = (level: GfsLevelSnapshot): string => {
    const monitorCount = monitorCountByLevelId[level.id] ?? 0;
    return `${level.name} (${monitorCount})`;
  };

  const noLevelDisplayLabel = `${t("Ohne Ebene")} (${noLevelMonitorCount})`;
  let selectedLevelLabel = t("Ebene auswählen...");
  if (selectedLevel) {
    selectedLevelLabel = toLevelLabel(selectedLevel);
  } else if (isNoLevelSelected) {
    selectedLevelLabel = noLevelDisplayLabel;
  }

  const noLevelOption: GfsLevelSnapshot = {
    id: "",
    name: noLevelDisplayLabel,
    type: createLevelType,
  };
  const selectedOption =
    selectedLevel ?? (isNoLevelSelected ? noLevelOption : null);

  return (
    <div className="grid items-start gap-1.5">
      <Label htmlFor="gfs-level-selector">{t("Ebene")}</Label>
      <Combobox
        value={selectedOption}
        inputValue={newLevelName}
        disabled={disabled || !buildingId || levels === undefined}
        onValueChange={(nextLevel) => {
          handleLevelSelect(nextLevel?.id ?? "");
          setNewLevelName("");
        }}
        onInputValueChange={setNewLevelName}
        itemToStringLabel={(level) => level?.name ?? ""}
        itemToStringValue={(level) => level?.name ?? ""}
      >
        <ComboboxInput
          id="gfs-level-selector"
          aria-label={t("Ebene")}
          className="h-10 w-full max-w-[250px]"
          placeholder={selectedLevelLabel}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              handleSubmit();
            }
          }}
        />
        <ComboboxContent className="w-[250px]">
          <ComboboxEmpty>
            <div className="flex flex-col gap-3 px-6 text-sm">
              <span>{t("Ebene nicht vorhanden.")}</span>
              <Button
                onClick={handleSubmit}
                variant="default"
                disabled={disabled || isCreating}
              >
                {t("Hinzufügen")}
              </Button>
            </div>
          </ComboboxEmpty>
          <ComboboxList>
            <ComboboxGroup>
              <ComboboxItem value={noLevelOption}>
                {noLevelDisplayLabel}
              </ComboboxItem>
              {levels.map((level) => (
                <ComboboxItem value={level} key={level.id}>
                  {toLevelLabel(level)}
                </ComboboxItem>
              ))}
            </ComboboxGroup>
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
    </div>
  );
}

type GfsAreaSelectorComboBoxProps = {
  areas?: GfsAreaSnapshot[];
  onAreaSelect: (area: GfsAreaSnapshot) => void;
  buildingId?: string;
  levelId?: string;
  areaType: GfsAreaType;
  triggerLabel: string;
  disabled?: boolean;
  onAreaCreate: (
    area: GfsAreaSnapshot,
  ) => boolean | void | Promise<boolean | void>;
};

function areaTypeLabel(areaType: string): string {
  switch (areaType) {
    case GFS_AREA_TYPE_INSIDE:
      return t("Innenbereich");
    case GFS_AREA_TYPE_OUTSIDE:
      return t("Außenbereich");
    default:
      return t("Nicht definiert");
  }
}

export function GfsAreaSelectorComboBox({
  areas,
  onAreaSelect,
  buildingId,
  levelId,
  areaType,
  triggerLabel,
  disabled = false,
  onAreaCreate,
}: GfsAreaSelectorComboBoxProps) {
  const [newAreaName, setNewAreaName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const isScopeMissing = !buildingId;

  const handleSubmit = async () => {
    const name = newAreaName.trim();
    if (!name || isScopeMissing || isCreating) return;

    const area: GfsAreaSnapshot = {
      id: v4(),
      levelId: levelId || undefined,
      name,
      type: areaType,
    };

    setIsCreating(true);
    try {
      const succeeded = await onAreaCreate(area);
      if (succeeded === false) return;
      setNewAreaName("");
    } finally {
      setIsCreating(false);
    }
  };

  const handleSelect = (area: GfsAreaSnapshot | null) => {
    if (!area) return;
    onAreaSelect(area);
    setNewAreaName("");
  };

  return (
    <Combobox<GfsAreaSnapshot>
      value={null}
      disabled={disabled || isScopeMissing || areas === undefined}
      onValueChange={handleSelect}
      onInputValueChange={setNewAreaName}
      itemToStringLabel={(area) => area?.name ?? ""}
      itemToStringValue={(area) => area?.name ?? ""}
    >
      <ComboboxInput
        aria-label={triggerLabel}
        className="h-10 w-full max-w-[250px]"
        placeholder={triggerLabel}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            handleSubmit();
          }
        }}
      />
      <ComboboxContent className="w-[250px]">
        <ComboboxEmpty>
          <div className="flex flex-col gap-3 px-6 text-sm">
            <span>{t("Bereich nicht vorhanden.")}</span>
            <Button
              onClick={handleSubmit}
              variant="default"
              disabled={
                disabled || !newAreaName || isScopeMissing || isCreating
              }
            >
              {t("Hinzufügen")}
            </Button>
          </div>
        </ComboboxEmpty>
        <ComboboxList>
          <ComboboxGroup>
            {areas?.map((area) => (
              <ComboboxItem value={area} key={area.id}>
                {area.name} ({areaTypeLabel(area.type)})
              </ComboboxItem>
            ))}
          </ComboboxGroup>
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}

export function countAssessmentMonitorsBySite(
  buildings: GfsBuildingSnapshot[],
  areaAssessments: GfsAreaAssessment[],
): {
  buildingCounts: Record<string, number>;
  levelCounts: Record<string, number>;
  noLevelCounts: Record<string, number>;
} {
  const areaMonitorCounts = new Map(
    areaAssessments.map((areaAssessment) => [
      areaAssessment.areaId,
      areaAssessment.monitors.reduce((sum, monitor) => sum + monitor.amount, 0),
    ]),
  );

  const buildingCounts: Record<string, number> = {};
  const levelCounts: Record<string, number> = {};
  const noLevelCounts: Record<string, number> = {};

  buildings.forEach((building) => {
    building.areas.forEach((area) => {
      const count = areaMonitorCounts.get(area.id) ?? 0;
      buildingCounts[building.id] = (buildingCounts[building.id] ?? 0) + count;
      if (area.levelId) {
        levelCounts[area.levelId] = (levelCounts[area.levelId] ?? 0) + count;
      } else {
        const key = `${building.id}:${area.type}`;
        noLevelCounts[key] = (noLevelCounts[key] ?? 0) + count;
      }
    });
  });

  return { buildingCounts, levelCounts, noLevelCounts };
}
